import {
  useSearchQueryFilter,
  useSearchTypeFromRoute,
  useTrackEvents,
} from '@aibees/elephant/hooks';
import { Maybe } from '@aibees/shared/definition';
import { classnames } from '@aibees/shared/utils';
import { Box, Card, CardContent } from '@mui/material';
import { Fragment, memo, useCallback, useMemo, useState } from 'react';
import { useEmployeeByDepartmentEmptyField } from '../../hooks/use-employee-by-department-empty-field';
import { useEmployeeByDepartmentIncompleteItem } from '../../hooks/use-employee-by-department-incomplete-item';
import {
  useEmployeeByDepartmentValuesFilteredItems,
  useEmployeeByDepartmentValuesValues,
} from '../../hooks/use-employee-by-department-values';
import {
  buildDraft,
  clearDepartmentValue,
  clearEmployeeByDepartmentItem,
} from '../../utils';
import { EmployeeByDepartmentItemFilterInput } from '../employee-by-department-item-filter-input';
import * as SX from './sx';
import { type EmployeeByDepartmentItemProps as Props } from './types';

export const EmployeeByDepartmentItem = memo(
  ({ className, index, field, items }: Props) => {
    const { params, setParam } = useSearchQueryFilter({
      select: [field],
    });
    const searchType = useSearchTypeFromRoute();
    const { track } = useTrackEvents();

    const values = useEmployeeByDepartmentValuesValues(
      params,
      index,
      field,
      items
    );
    const filteredItems = useEmployeeByDepartmentValuesFilteredItems(
      params,
      index,
      field,
      items
    );
    const isIncompleteItem = useEmployeeByDepartmentIncompleteItem(values);
    const isAnyEmptyField = useEmployeeByDepartmentEmptyField(values);
    const [isCardHovered, setIsCardHovered] = useState(false);

    const handleChange = useCallback(
      (segmentIndex: number, value: Maybe<string>) => {
        const draft = buildDraft(params, field, index, segmentIndex, value);
        setParam(field, draft);
        track('FILTER', {
          filterName: field,
          filterValue: draft,
          searchType,
        });
      },
      [params, field, index, setParam, track, searchType]
    );

    const handleClearItem = useCallback(() => {
      const draft = clearEmployeeByDepartmentItem(params, field, index);
      track('FILTER', {
        filterName: field,
        filterValue: draft,
        searchType,
      });
      setParam(field, draft);
    }, [params, setParam, field, index, track, searchType]);

    const handleClearDepartmentValue = useCallback(
      (valueToRemove: string) => {
        const draft = clearDepartmentValue(params, field, valueToRemove);
        setParam(field, draft);
      },
      [params, setParam, field]
    );

    const showClearButton = useMemo(
      () => isCardHovered && !isAnyEmptyField,
      [isCardHovered, isAnyEmptyField]
    );

    const setCardHoveredTrue = useCallback(() => setIsCardHovered(true), []);
    const setCardHoveredFalse = useCallback(() => setIsCardHovered(false), []);

    return (
      <Box
        className={classnames('EmployeeByDepartmentItem', className)}
        display="flex"
        flexDirection="column"
        gap={1}
        sx={SX.root}
      >
        <Card
          variant="filled"
          color="default"
          onMouseEnter={setCardHoveredTrue}
          onMouseLeave={setCardHoveredFalse}
        >
          <CardContent padding="none">
            {filteredItems.map((item, i) => (
              <Fragment key={i}>
                <EmployeeByDepartmentItemFilterInput
                  onClear={handleClearDepartmentValue}
                  filter={item}
                  onChange={(value) => handleChange(i, value)}
                  value={values?.[i]}
                  error={isIncompleteItem && !values?.[i]}
                  parentField={field}
                  onClearButtonClick={handleClearItem}
                  showClearButton={showClearButton}
                />
              </Fragment>
            ))}
          </CardContent>
        </Card>
      </Box>
    );
  }
);

EmployeeByDepartmentItem.displayName = 'EmployeeByDepartmentItem';
